import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { ArrowLeftIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSnackbar } from '../../../utils/useSnackbar';
import { breakpoints } from '../../../theme/theme';
import styles from '../../../styles/login/rightSection.module.scss';
import { PasswordSchema, UserEmailSchema } from '../../../Validation/ReusableValidation';
import { completeSignup } from '../../../utils/api/signup_apis';
import { STATUS } from '../../../utils/api/sharedfunction';
import LegalConsentCheckbox from '../../../components/consent/LegalConsentCheckbox';
import { CONSENT_COPY } from '../../../constants/consentCopy';
const SignupCompleteSchema = z.object({
  email: UserEmailSchema,
  orgName: z
    .string()
    .min(1, 'Organization name is required')
    .refine((v) => v.trim().length > 0, 'Organization name cannot be only spaces'),
  fullName: z
    .string()
    .min(1, 'Full name is required')
    .refine((v) => v.trim().length > 0, 'Full name cannot be only spaces'),
  password: PasswordSchema,
});

const sharedTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '0.5rem',
    bgcolor: '#fff',
    fontSize: '1rem',
    color: '#0f172a',
    transition: 'all 0.2s',
    boxShadow: 'none',
    border: 'none',
    '& fieldset': {
      borderRadius: '0.5rem',
      borderColor: '#CBD5E1',
    },
    '&:hover fieldset': {
      borderColor: '#3b82f6',
      borderRadius: '0.5rem',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
      borderWidth: 2,
      borderRadius: '0.5rem',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#CBD5E1',
    borderWidth: 1,
    borderRadius: '0.5rem',
    transition: 'border-color 0.2s, border-width 0.2s, border-radius 0.2s',
  },
  '& .MuiOutlinedInput-input': {
    height: '1.2rem',
    [`@media (min-width:${breakpoints.xl}px)`]: {
      height: '1.625rem',
    },
  },
  '& .MuiInputBase-input': {
    padding: '12px 16px',
    backgroundColor: '#fff',
    fontSize: '0.7rem',
    [`@media (min-width:${breakpoints.xl}px)`]: {
      fontSize: '1rem',
    },
    color: '#0f172a',
    lineHeight: '1.5rem',
    fontWeight: 400,
    transition: 'all 0.2s',
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#64748b',
    opacity: 1,
    fontSize: '13px',
    lineHeight: '1rem',
    fontWeight: 400,
  },
  '& .MuiFormHelperText-root': {
    fontSize: '0.7rem',
    lineHeight: '1rem',
    color: '#ef4444',
  },
};

export default function SignupStep2Complete({ email, verificationToken, onSuccess, onBack }) {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [consentMeta, setConsentMeta] = useState({ ready: false });
  const [consentError, setConsentError] = useState(null);
  const { showSnackbar } = useSnackbar();

  const handleConsentChange = useCallback((meta) => {
    setConsentAgreed(meta.agreed);
    setConsentMeta(meta);
    if (meta.agreed) {
      setConsentError(null);
    }
  }, []);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(SignupCompleteSchema),
    defaultValues: {
      email: email ?? '',
      orgName: '',
      fullName: '',
      password: '',
    },
  });

  const onSubmit = async (data) => {
    if (!verificationToken || !verificationToken.trim()) {
      showSnackbar('Verification link is missing. Please use the link from your email.', 'error');
      return;
    }
    if (!consentAgreed) {
      setConsentError(CONSENT_COPY.accountValidationError);
      return;
    }
    if (!consentMeta.ready) {
      setConsentError(consentMeta.error || 'Legal documents are not available. Please try again later.');
      return;
    }
    setLoading(true);
    try {
      const response = await completeSignup({
        verificationToken: verificationToken.trim(),
        orgName: data.orgName.trim(),
        fullName: data.fullName.trim(),
        password: data.password,
        tosLegalDocumentVersionId: consentMeta.tosLegalDocumentVersionId,
        privacyLegalDocumentVersionId: consentMeta.privacyLegalDocumentVersionId,
      });

      if (response.status === STATUS.SUCCESS) {
        onSuccess?.(response?.data?.user ?? response?.data);
      } else {
        showSnackbar(response.error || 'Signup failed. Please try again.', 'error');
      }
    } catch (err) {
      showSnackbar(err?.error || 'Signup failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box className={styles['form-header']}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <IconButton
            onClick={onBack}
            aria-label="Go back"
            sx={{
              p: 0.5,
              color: '#64748b',
              flexShrink: 0,
              '&:hover': {
                color: '#3b82f6',
                backgroundColor: 'rgba(59, 130, 246, 0.04)',
              },
            }}
          >
            <ArrowLeftIcon style={{ width: 20, height: 20 }} />
          </IconButton>
          <h6 className={styles['form-title']} style={{ margin: 0, flex: 1 }}>
            Complete your signup
          </h6>
        </Box>
        <p className={styles['form-subtitle']}>
          Enter your details to create your organization and account.
        </p>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} className={styles['form']}>
        <Box className={styles['form-field']}>
          <h6 className={styles['field-label']}>Email address</h6>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id="complete-email"
                type="email"
                variant="outlined"
                fullWidth
                size="small"
                disabled
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={{
                  ...sharedTextFieldSx,
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: '#64748b',
                    backgroundColor: '#f1f5f9',
                  },
                }}
              />
            )}
          />
        </Box>
        <Box className={styles['form-field']}>
          <h6 className={styles['field-label']}>Organization name</h6>
          <Controller
            name="orgName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id="complete-orgName"
                type="text"
                placeholder="Enter your organization name"
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.orgName}
                helperText={errors.orgName?.message}
                sx={sharedTextFieldSx}
              />
            )}
          />
        </Box>
        <Box className={styles['form-field']}>
          <h6 className={styles['field-label']}>Full name</h6>
          <Controller
            name="fullName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id="complete-fullName"
                type="text"
                placeholder="Enter your full name"
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.fullName}
                helperText={errors.fullName?.message}
                sx={sharedTextFieldSx}
              />
            )}
          />
        </Box>
        <Box className={styles['form-field']}>
          <h6 className={styles['field-label']}>Password</h6>
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id="complete-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Create a password"
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.password}
                helperText={errors.password?.message}
                sx={{
                  ...sharedTextFieldSx,
                  '& .MuiOutlinedInput-root': {
                    ...sharedTextFieldSx['& .MuiOutlinedInput-root'],
                    borderRadius: '0.5rem 0 0 0.5rem',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((p) => !p)}
                        edge="end"
                      >
                        {showPassword ? (
                          <EyeSlashIcon style={{ width: 20, height: 20 }} />
                        ) : (
                          <EyeIcon style={{ width: 20, height: 20 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Box>
        <Box className={styles['form-field']} sx={{ mt: 1 }}>
          <LegalConsentCheckbox
            checked={consentAgreed}
            onChange={handleConsentChange}
            disabled={loading}
            error={consentError}
            id="signup-consent"
          />
        </Box>
        <Button
          type="submit"
          variant="primaryBtn"
          fullWidth
          disabled={loading || !isValid || !consentMeta.ready || !consentAgreed}
          className={styles['submit-button']}
        >
          {loading ? (
            <CircularProgress size={20} className={styles['submit-button-loading']} />
          ) : (
            'Sign up'
          )}
        </Button>
        <Button
          type="button"
          variant="text"
          fullWidth
          onClick={onBack}
          disabled={loading}
          sx={{
            mt: 1,
            color: '#64748b',
            fontSize: '0.75rem',
            fontWeight: 400,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(100, 116, 139, 0.04)',
              color: '#475569',
            },
            [`@media (min-width:${breakpoints.xl}px)`]: {
              fontSize: '0.875rem',
            },
          }}
        >
          Use a different email
        </Button>
      </Box>
    </>
  );
}
